/** **Regenerate deep link** (plan D6, §5 "Header", §9.8): Branch.io makes the itinerary's deep
 *  link again — an existing one keeps its address and gets the current name and cover, a
 *  missing one is made — and its QR code is stored from it; the app's catalog entry refreshes
 *  after that. One step, in the Actions menu's Links group (**Regenerate deep link now**) and
 *  as **Regenerate now** where the Identity card says a link or a stored QR image is missing: a
 *  confirmation naming what happens, `POST …/deep_link/regenerate/`, then the receipt with the
 *  audit row.
 *
 *  The server answers 202 — the work is queued, with no task to follow — so the page reloads
 *  the detail every few seconds for a short while (`useDeepLinkWatch`) and the deep link row
 *  says Branch.io is at work meanwhile; the link and the QR code show as soon as they change.
 *  A missing link is watched until its QR code is stored too; one that has not come after the
 *  window says to reload in a minute. An existing link keeps its address, so nothing may
 *  visibly change: at the window's end the row says once, for a few seconds, what was asked
 *  ("Asked Branch.io to refresh it — the address stays the same.") — never that it is done.
 *
 *  Offered to `console_itineraries_manage`, in both modes (it leaves the draft alone); disabled
 *  with the reason while editing is switched off or the itinerary has no operator; a
 *  placeholder's header keeps it locked. Refused, nothing done: the operator has no Branch.io
 *  key and secret (the server's words), editing switched off, someone else saving (Try again),
 *  any other refusal in the server's words — its own mapping, with no "already so"
 *  (`itineraryRegenerateRefusal`). */
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { CircularProgress, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import * as itinerariesApi from '@/api/itineraries';
import type { ItineraryDetail } from '@/api/itineraries';
import {
  itineraryRegenerateRefusal,
  useRegenerateItineraryDeepLink,
  type ItineraryDeepLinkRegenerateAccepted,
} from '@/api/itineraryActions';
import { itineraryDetailKey } from '@/api/itineraryRecord';
import { CATALOG_EDITING_OFF } from '@/features/catalog/editGate';
import type { RecordActionItem } from '@/features/records/page/RecordActionsMenu';
import { auditHref } from '@/utils/gridLinks';
import { formatZoned } from '@/utils/zonedTime';

import { ITINERARY_TIMEZONE } from '../editorConfig';
import { ITINERARY_ACTION, itineraryTitle } from '../parts/ItineraryRecordHeader';

import { ItineraryNowPopover, type ItineraryNowReceipt, type ItineraryNowRefusal } from './ItineraryNowPopover';

/** The Actions item and its confirmation's title — it happens at once, so it ends in "now"
 *  (the Draft-Or-Now Rule). */
export const REGENERATE_DEEP_LINK = 'Regenerate deep link now';
/** Where the Identity card says a link or a stored QR image is missing. */
export const REGENERATE_NOW = 'Regenerate now';

/** How often the detail is reloaded after a regenerate, for how long, and how long the
 *  closing line of an existing link's refresh stays (tests shorten them). */
export const DEEP_LINK_WATCH = { pollMs: 3_000, windowMs: 30_000, closingMs: 6_000 };

/** The closing line of an existing link's refresh: what was asked, never that it is done. */
export const DEEP_LINK_ASKED = 'Asked Branch.io to refresh it — the address stays the same.';

/** What happens, before it happens — the server's consequences, word for word. */
export function regenerateConsequences(hasLink: boolean): string[] {
  return [
    hasLink
      ? 'Branch.io updates the deep link in a few seconds: it keeps its address and gets the itinerary’s current name and cover.'
      : 'Branch.io makes the deep link in a few seconds.',
    'Its QR code is made again from it, and the app’s catalog entry refreshes after that.',
  ];
}

/** Why Regenerate deep link did nothing, in the operator's terms. */
export function regenerateRefusal(e: unknown): ItineraryNowRefusal {
  const r = itineraryRegenerateRefusal(e);
  switch (r.kind) {
    case 'locked':
      return { text: 'someone else is saving this itinerary right now — try again.', retry: true };
    case 'disabled':
      return { text: `${CATALOG_EDITING_OFF.toLowerCase()} on this environment.`, retry: false };
    default:
      // The operator without Branch.io keys, the placeholder: the server's own words.
      return { text: r.detail, retry: r.kind === 'failed' };
  }
}

/** The receipt: "Asked Branch.io 8 Oct 09:30 UTC · audit #99411", the server's consequences
 *  and the audit row. `askedAt`: when the 202 came (the answer carries no time). */
export function regenerateReceipt(r: ItineraryDeepLinkRegenerateAccepted, askedAt: string): ItineraryNowReceipt {
  return {
    headline: `Asked Branch.io ${formatZoned(askedAt, ITINERARY_TIMEZONE, { weekday: false })} · audit #${r.audit_id}`,
    lines: r.consequences,
    links: [{ label: 'Audit row', to: auditHref({ task_id: r.audit_uuid }) }],
  };
}

/** A regenerate the page is waiting on: when it was asked, and whether a link was there. */
export interface DeepLinkWatch {
  askedAt: string;
  hadLink: boolean;
  /** A missing link that has come — its QR code not stored yet. */
  linkMade: boolean;
  /** `watching`: the detail reloads every few seconds; `late`: a missing link (or its QR code)
   *  did not come within the window — reload in a minute; `asked`: an existing link's window
   *  ended unchanged — the closing line, for a few seconds. */
  phase: 'watching' | 'late' | 'asked';
}

interface WatchState extends DeepLinkWatch {
  link: string | null;
  qr: string | null;
}

const linkOf = (d: Pick<ItineraryDetail, 'branchio_link'>) => d.branchio_link?.trim() || null;
const qrOf = (d: Pick<ItineraryDetail, 'qr_image_url'>) => d.qr_image_url?.trim() || null;

/** After a 202: reload the detail every `pollMs` until its link or stored QR image differs from
 *  what the 202 said was there before — a missing link, until both do (the link may come
 *  before its QR code) — for at most `windowMs`. Then an existing link says what was asked for
 *  `closingMs` (`asked`: it keeps its address, so nothing visible may change); a missing one
 *  that has not come, or its QR code, is `late`. */
export function useDeepLinkWatch(detail: ItineraryDetail): {
  watch: DeepLinkWatch | null;
  start: (accepted: ItineraryDeepLinkRegenerateAccepted, askedAt: string) => void;
} {
  const [state, setState] = useState<WatchState | null>(null);
  const watching = state?.phase === 'watching';
  useQuery({
    queryKey: itineraryDetailKey(detail.id),
    queryFn: () => itinerariesApi.retrieve(detail.id),
    enabled: watching,
    refetchInterval: watching ? DEEP_LINK_WATCH.pollMs : false,
  });

  // The link or the QR code moved: Branch.io is done — but a missing link that came before its
  // QR code is watched on for the QR code.
  useEffect(() => {
    if (!state) return;
    const link = linkOf(detail);
    const linkMoved = link !== state.link;
    const qrMoved = qrOf(detail) !== state.qr;
    if (!linkMoved && !qrMoved) return;
    if (!state.hadLink && link !== null && !qrMoved) {
      if (!state.linkMade) setState({ ...state, linkMade: true });
      return;
    }
    setState(null);
  }, [detail, state]);

  // The window.
  useEffect(() => {
    if (!watching) return undefined;
    const t = setTimeout(
      () => setState((s) => (s ? { ...s, phase: s.hadLink ? 'asked' : 'late' } : null)),
      DEEP_LINK_WATCH.windowMs,
    );
    return () => clearTimeout(t);
  }, [watching]);

  // An existing link's closing line, for a few seconds.
  const asked = state?.phase === 'asked';
  useEffect(() => {
    if (!asked) return undefined;
    const t = setTimeout(() => setState((s) => (s?.phase === 'asked' ? null : s)), DEEP_LINK_WATCH.closingMs);
    return () => clearTimeout(t);
  }, [asked]);

  const watch = useMemo<DeepLinkWatch | null>(
    () => (state ? { askedAt: state.askedAt, hadLink: state.hadLink, linkMade: state.linkMade, phase: state.phase } : null),
    [state],
  );
  const start = useCallback((accepted: ItineraryDeepLinkRegenerateAccepted, askedAt: string) => {
    const link = accepted.branchio_link?.trim() || null;
    setState({ askedAt, hadLink: link !== null, linkMade: false, phase: 'watching', link, qr: accepted.qr_image_url?.trim() || null });
  }, []);
  return { watch, start };
}

/** The deep link row's line while the page waits on Branch.io — polite, never an alert. */
export function DeepLinkWatchStatus({ watch }: { watch: DeepLinkWatch | null }) {
  if (!watch) return null;
  const at = formatZoned(watch.askedAt, ITINERARY_TIMEZONE, { weekday: false });
  return (
    <Typography
      variant="caption"
      color="text.secondary"
      role="status"
      data-deep-link-watch={watch.phase}
      sx={{ display: 'flex', gap: 0.5, alignItems: 'center', mt: 0.25 }}
    >
      {watch.phase === 'watching' ? (
        <>
          <CircularProgress size={12} aria-hidden sx={{ flex: 'none' }} />
          {watch.hadLink
            ? `Branch.io is updating the deep link and its QR code (asked ${at}).`
            : watch.linkMade
              ? `Branch.io made the deep link and is storing its QR code (asked ${at}).`
              : `Branch.io is making the deep link (asked ${at}) — it shows here once it is made.`}
        </>
      ) : (
        <>
          <InfoOutlinedIcon aria-hidden sx={{ fontSize: 14, flex: 'none' }} />
          {watch.phase === 'asked'
            ? DEEP_LINK_ASKED
            : watch.linkMade
              ? `Branch.io hasn’t stored the QR code yet (asked ${at}). Reload the page in a minute.`
              : `Branch.io hasn’t answered yet (asked ${at}). Reload the page in a minute.`}
        </>
      )}
    </Typography>
  );
}

/** Regenerate deep link for the page: the Actions item, `ask` for the Identity card's
 *  Regenerate now (null when it is not offered), the popover and the watch. */
export function useRegenerateDeepLink(
  detail: ItineraryDetail,
  { canManage, blockedReason }: { canManage: boolean; blockedReason: string | null },
): {
  item: RecordActionItem | null;
  ask: ((anchorEl: HTMLElement | null) => void) | null;
  popover: ReactNode;
  watch: DeepLinkWatch | null;
} {
  const [open, setOpen] = useState<{ anchorEl: HTMLElement | null; lines: string[] } | null>(null);
  const mutation = useRegenerateItineraryDeepLink(detail.id);
  const { watch, start } = useDeepLinkWatch(detail);
  // The header locks it on a placeholder; the card offers no Regenerate now there.
  const offered = canManage && !blockedReason && !detail.placeholder;
  // Stable for the page's display context; the consequences read the detail when it opens.
  const latest = useRef(detail);
  latest.current = detail;
  const ask = useCallback(
    (anchorEl: HTMLElement | null) => setOpen({ anchorEl, lines: regenerateConsequences(linkOf(latest.current) !== null) }),
    [],
  );
  const item: RecordActionItem | null = canManage
    ? {
        key: ITINERARY_ACTION.regenerateDeepLink,
        label: REGENERATE_DEEP_LINK,
        immediate: true,
        ...(blockedReason ? { disabled: true, description: blockedReason } : { hint: 'Branch.io', onSelect: ask }),
      }
    : null;
  const popover = open ? (
    <ItineraryNowPopover
      anchorEl={open.anchorEl}
      onClose={() => setOpen(null)}
      title={REGENERATE_DEEP_LINK}
      subject={itineraryTitle(detail)}
      lines={open.lines}
      confirmLabel={REGENERATE_NOW}
      run={async () => {
        const accepted = await mutation.mutateAsync();
        const askedAt = new Date().toISOString();
        start(accepted, askedAt);
        return { accepted, askedAt };
      }}
      receipt={({ accepted, askedAt }) => regenerateReceipt(accepted, askedAt)}
      refusal={regenerateRefusal}
      dataKey="regenerate_deep_link"
    />
  ) : null;
  return { item, ask: offered ? ask : null, popover, watch };
}
